import LegalLayout from "@/layouts/LegalLayout";

export default function PrivacyPage() {
  return (
    <LegalLayout title="Privacy Policy">
      <p>
        This Privacy Policy describes how MinutePay handles information when you use the MinutePay
        website, account, and browser extension. It reflects the product as it exists today.
      </p>

      <h2>1. Information we handle</h2>
      <p>Depending on how you use MinutePay, we may process:</p>
      <ul>
        <li>
          <strong>Account information</strong>, such as name, email address, password (stored as a
          password hash, not in plain text), and role (for example user or admin).
        </li>
        <li>
          <strong>Authentication data</strong>, such as session tokens used to keep you signed in.
        </li>
        <li>
          <strong>Interpreter work metadata</strong> you or the extension record, which can include
          call identifiers, start and end times, duration, platform or company, pay rate, currency,
          and calculated earnings, plus related history and goal information.
        </li>
        <li>
          <strong>Billing and subscription status</strong>, such as whether a license or trial is
          active, trial dates, and payment-provider identifiers needed to match a subscription to
          your account. MinutePay does <strong>not</strong> store full card numbers. Card and
          payment-method details are processed by Paddle.
        </li>
        <li>
          <strong>Local browser and extension storage</strong>, which may include settings, login
          state for the extension, license cache flags, and locally saved call records before or
          alongside cloud sync.
        </li>
      </ul>

      <h2>2. Where information is stored</h2>
      <p>
        Account, billing status, and synced call history are stored on MinutePay’s backend/cloud
        database so you can use the dashboard across devices. The browser extension also stores
        information locally on your device. You should treat that device as you would any device
        that holds work records.
      </p>

      <h2>3. How we use information</h2>
      <p>We use this information to:</p>
      <ul>
        <li>create and authenticate your account;</li>
        <li>operate tracking, history, earnings, goals, and dashboard features;</li>
        <li>sync records you choose to store in the cloud;</li>
        <li>apply trial and paid-subscription access;</li>
        <li>process billing through payment providers;</li>
        <li>respond to account or billing requests you send us;</li>
        <li>maintain and debug the service.</li>
      </ul>
      <p>We do not sell your personal information.</p>

      <h2>4. Service providers</h2>
      <p>
        MinutePay uses infrastructure and payment partners to run the product. That currently
        includes hosting/database providers for the website and API, and Paddle for subscriptions.
        Those providers process information only as needed to provide their services to MinutePay.
      </p>

      <h2>5. Security</h2>
      <p>
        We use standard account protections such as hashed passwords and authenticated API access.
        No method of transmission or storage is completely secure. You should use a strong unique
        password and sign out on shared devices.
      </p>

      <h2>6. Retention</h2>
      <p>
        We keep account and work records while your account is open and as needed to operate the
        service, resolve billing questions, or meet legal obligations. Local extension data remains
        on your device until you clear it or uninstall the extension.
      </p>

      <h2>7. Your choices</h2>
      <p>
        You can update account details in MinutePay where those controls exist. You may request
        correction or deletion of your account or stored work records by contacting MinutePay from
        the email address on your account after signing in. We may need to verify the request. We
        may retain limited information if required for security, billing disputes, or law.
      </p>

      <h2>8. Cookies and local storage</h2>
      <p>
        MinutePay uses local storage (and similar browser storage) to keep you signed in and to save
        extension settings and local call data. The website is a single-page application and may use
        ordinary cookies or local storage required to run the app. MinutePay does not use this to
        run a third-party advertising network.
      </p>

      <h2>9. Changes</h2>
      <p>
        We may update this policy as the product changes. The “Last updated” date at the top will
        change when we do. Continued use after an update means you accept the revised policy.
      </p>

      <h2>10. Contact</h2>
      <p>
        For privacy requests, sign in to MinutePay and write from the email address on your account,
        or use Billing for subscription-related questions. MinutePay does not currently publish a
        separate public support inbox in this product.
      </p>
    </LegalLayout>
  );
}
